import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  createProjectVehicleFuelRecord,
  deleteProjectVehicleFuelRecord,
  fetchProjectVehicleFuelRecords,
  updateProjectVehicleFuelRecord,
} from "../api";
import { getApiErrorMessage } from "../api/client";
import {
  exportVehicleRowsToExcel,
  toExcelNumber,
} from "../utils/vehicleExcelExport";
import { resizeImageFileToDataUrl } from "../utils/imageUpload";

function getTodayDateValue() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDateLabel(dateValue) {
  if (!dateValue) return "-";
  return new Intl.DateTimeFormat("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(`${dateValue}T00:00:00`));
}

function toKmNumber(value) {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
}

function compareFuelRecordOrder(left, right) {
  if (left.date !== right.date) return left.date.localeCompare(right.date);
  const createdDifference = Number(left.createdAt || 0) - Number(right.createdAt || 0);
  if (createdDifference !== 0) return createdDifference;
  return String(left.id || "").localeCompare(String(right.id || ""));
}

export default function FuelWorkspacePage({
  title,
  description,
  onBack,
  canDeleteRecords,
  currentUserId,
  selectedProjectId,
  isOnline,
}) {
  const todayDate = getTodayDateValue();
  const [vehicles, setVehicles] = useState([]);
  const [records, setRecords] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [recordDate, setRecordDate] = useState(todayDate);
  const [kmDraft, setKmDraft] = useState("");
  const [fuelAmountDraft, setFuelAmountDraft] = useState("");
  const [receiptNoDraft, setReceiptNoDraft] = useState("");
  const [totalAmountDraft, setTotalAmountDraft] = useState("");
  const [receiptImageUrl, setReceiptImageUrl] = useState("");
  const [saveNotice, setSaveNotice] = useState("");
  const [saveNoticeKind, setSaveNoticeKind] = useState("");
  const [editRecord, setEditRecord] = useState(null);
  const [photoNotice, setPhotoNotice] = useState("");
  const [exportStartDate, setExportStartDate] = useState(todayDate);
  const [exportEndDate, setExportEndDate] = useState(todayDate);
  const [exportNotice, setExportNotice] = useState("");

  const loadFuelWorkspace = useCallback(async () => {
    if (!selectedProjectId) return;
    if (!isOnline) {
      setSaveNotice("Çevrimdışı modda yakıt kilometre sırası değiştirilemez. Bağlantı gelince tekrar deneyin.");
      setSaveNoticeKind("info");
      return;
    }

    setIsLoading(true);
    try {
      const data = await fetchProjectVehicleFuelRecords(selectedProjectId);
      const nextVehicles = Array.isArray(data?.vehicles) ? data.vehicles : [];
      const nextRecords = Array.isArray(data?.records) ? data.records : [];
      const activeVehicle = nextVehicles.find((item) => item.active !== false);
      const fallbackVehicle = activeVehicle || nextVehicles[0] || null;

      setVehicles(nextVehicles);
      setRecords(nextRecords);
      setSelectedVehicleId((previous) => {
        if (nextVehicles.some((item) => item.id === previous)) {
          return previous;
        }
        return fallbackVehicle?.id || null;
      });
      setRecordDate(todayDate);
      setKmDraft("");
      setFuelAmountDraft("");
      setReceiptNoDraft("");
      setTotalAmountDraft("");
      setReceiptImageUrl("");
      setSaveNotice("");
      setSaveNoticeKind("");
      setPhotoNotice("");
      setExportStartDate(todayDate);
      setExportEndDate(todayDate);
      setExportNotice("");
    } catch (error) {
      setSaveNotice(getApiErrorMessage(error, "Yakıt kayıtları yüklenemedi."));
      setSaveNoticeKind("error");
    } finally {
      setIsLoading(false);
    }
  }, [isOnline, selectedProjectId, todayDate]);

  useEffect(() => {
    loadFuelWorkspace();
  }, [loadFuelWorkspace]);

  const activeVehicles = useMemo(
    () => vehicles.filter((vehicle) => vehicle.active !== false),
    [vehicles]
  );

  const archivedVehicles = useMemo(
    () => vehicles.filter((vehicle) => vehicle.active === false),
    [vehicles]
  );

  const selectedVehicle = useMemo(
    () => vehicles.find((item) => item.id === selectedVehicleId) || null,
    [selectedVehicleId, vehicles]
  );

  const selectedVehicleRecords = useMemo(
    () =>
      records
        .filter((record) => record.vehicleId === selectedVehicleId)
        .sort((left, right) => right.date.localeCompare(left.date)),
    [records, selectedVehicleId]
  );

  const handleReceiptImageChange = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const dataUrl = await resizeImageFileToDataUrl(file);
      setReceiptImageUrl(dataUrl);
      setPhotoNotice("Fiş fotoğrafı hazırlandı.");
    } catch (error) {
      setPhotoNotice(getApiErrorMessage(error, "Fiş fotoğrafı okunamadı."));
    } finally {
      event.target.value = "";
    }
  }, []);

  const handleSaveFuelRecord = useCallback(async () => {
    if (!isOnline) {
      setSaveNotice("Yakıt kilometre sırası sunucuda doğrulandığı için bu işlem bağlantı gerektirir.");
      setSaveNoticeKind("error");
      return;
    }
    if (!selectedVehicleId || !selectedVehicle) return;
    if (!recordDate) {
      setSaveNotice("Kayıt tarihi seçin.");
      setSaveNoticeKind("error");
      return;
    }
    if (selectedVehicle.active === false) {
      setSaveNotice("Pasif araç için yeni yakıt kaydı açılamaz.");
      setSaveNoticeKind("error");
      return;
    }

    const km = toKmNumber(kmDraft);
    if (km == null) {
      setSaveNotice("Yakıt kaydı için km zorunludur.");
      setSaveNoticeKind("error");
      return;
    }

    const previousKmValues = selectedVehicleRecords
      .filter((record) => record.date <= recordDate)
      .map((record) => toKmNumber(record.km))
      .filter((value) => value != null);
    const previousKm =
      previousKmValues.length > 0 ? Math.max(...previousKmValues) : null;
    if (previousKm != null && km <= previousKm) {
      setSaveNotice(
        `Yakıt km değeri, önceki ${previousKm} km değerinden büyük olmalıdır.`
      );
      setSaveNoticeKind("error");
      return;
    }

    const nextKmValues = selectedVehicleRecords
      .filter((record) => record.date > recordDate)
      .map((record) => toKmNumber(record.km))
      .filter((value) => value != null);
    const nextKm = nextKmValues.length > 0 ? Math.min(...nextKmValues) : null;
    if (nextKm != null && km >= nextKm) {
      setSaveNotice(
        `Yakıt km değeri, sonraki ${nextKm} km değerinden küçük olmalıdır.`
      );
      setSaveNoticeKind("error");
      return;
    }

    try {
      const savedRecord = await createProjectVehicleFuelRecord(selectedProjectId, {
        vehicleId: selectedVehicleId,
        date: recordDate,
        km: kmDraft,
        fuelAmount: fuelAmountDraft,
        receiptNo: receiptNoDraft,
        totalAmount: totalAmountDraft,
        receiptImageUrl,
      });
      setRecords((previous) => [savedRecord, ...previous]);
      setKmDraft("");
      setFuelAmountDraft("");
      setReceiptNoDraft("");
      setTotalAmountDraft("");
      setReceiptImageUrl("");
      setPhotoNotice("");
      setSaveNotice("Yakıt satırı veritabanına kaydedildi.");
      setSaveNoticeKind("success");
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, "Yakıt satırı kaydedilemedi.");
      await loadFuelWorkspace();
      setSaveNotice(errorMessage);
      setSaveNoticeKind("error");
    }
  }, [
    fuelAmountDraft,
    isOnline,
    kmDraft,
    loadFuelWorkspace,
    receiptImageUrl,
    receiptNoDraft,
    recordDate,
    selectedProjectId,
    selectedVehicle,
    selectedVehicleId,
    selectedVehicleRecords,
    totalAmountDraft,
  ]);

  const handleStartRecordEdit = useCallback((record) => {
    setEditRecord({
      id: record.id,
      km: record.km,
      fuelAmount: record.fuelAmount,
      totalAmount: record.totalAmount,
    });
    setSaveNotice("");
    setSaveNoticeKind("");
  }, []);

  const handleSaveRecordEdit = useCallback(async () => {
    if (!isOnline) {
      setSaveNotice("Yakıt kaydı düzeltmesi bağlantı gerektirir.");
      setSaveNoticeKind("error");
      return;
    }
    if (!editRecord) return;
    const currentRecord = selectedVehicleRecords.find(
      (record) => record.id === editRecord.id
    );
    if (!currentRecord) return;

    const km = toKmNumber(editRecord.km);
    if (km == null) {
      setSaveNotice("Yakıt kaydı için km zorunludur.");
      setSaveNoticeKind("error");
      return;
    }
    if (!String(editRecord.fuelAmount || "").trim()) {
      setSaveNotice("Yakıt miktarı zorunludur.");
      setSaveNoticeKind("error");
      return;
    }
    if (!String(editRecord.totalAmount || "").trim()) {
      setSaveNotice("Toplam tutar zorunludur.");
      setSaveNoticeKind("error");
      return;
    }

    const otherRecords = selectedVehicleRecords.filter(
      (record) => record.id !== currentRecord.id
    );
    const previousKmValues = otherRecords
      .filter((record) => compareFuelRecordOrder(record, currentRecord) < 0)
      .map((record) => toKmNumber(record.km))
      .filter((value) => value != null);
    const previousKm =
      previousKmValues.length > 0 ? Math.max(...previousKmValues) : null;
    if (previousKm != null && km <= previousKm) {
      setSaveNotice(
        `Yakıt km değeri, önceki ${previousKm} km değerinden büyük olmalıdır.`
      );
      setSaveNoticeKind("error");
      return;
    }

    const nextKmValues = otherRecords
      .filter((record) => compareFuelRecordOrder(record, currentRecord) > 0)
      .map((record) => toKmNumber(record.km))
      .filter((value) => value != null);
    const nextKm = nextKmValues.length > 0 ? Math.min(...nextKmValues) : null;
    if (nextKm != null && km >= nextKm) {
      setSaveNotice(
        `Yakıt km değeri, sonraki ${nextKm} km değerinden küçük olmalıdır.`
      );
      setSaveNoticeKind("error");
      return;
    }

    setIsLoading(true);
    try {
      const updatedRecord = await updateProjectVehicleFuelRecord(
        selectedProjectId,
        currentRecord.id,
        {
          km,
          fuelAmount: editRecord.fuelAmount,
          totalAmount: editRecord.totalAmount,
        }
      );
      setRecords((previous) =>
        previous.map((record) =>
          record.id === currentRecord.id ? updatedRecord : record
        )
      );
      setEditRecord(null);
      setSaveNotice("Yakıt kaydındaki sayılar düzeltildi.");
      setSaveNoticeKind("success");
    } catch (error) {
      setSaveNotice(getApiErrorMessage(error, "Yakıt sayıları düzeltilemedi."));
      setSaveNoticeKind("error");
    } finally {
      setIsLoading(false);
    }
  }, [editRecord, isOnline, selectedProjectId, selectedVehicleRecords]);

  const handleDeleteRecord = useCallback(async (record) => {
    if (!isOnline) {
      setSaveNotice("Yakıt kaydı silme işlemi bağlantı gerektirir.");
      setSaveNoticeKind("error");
      return;
    }
    if (!canDeleteRecords) return;
    const confirmed = window.confirm(
      `${formatDateLabel(record.date)} tarihli yakıt kaydı silinsin mi? Bu işlem geri alınamaz.`
    );
    if (!confirmed) return;

    setIsLoading(true);
    try {
      await deleteProjectVehicleFuelRecord(selectedProjectId, record.id);
      setRecords((previous) =>
        previous.filter((item) => item.id !== record.id)
      );
      setEditRecord((previous) => (previous?.id === record.id ? null : previous));
      setSaveNotice("Yakıt kaydı silindi.");
      setSaveNoticeKind("success");
    } catch (error) {
      setSaveNotice(getApiErrorMessage(error, "Yakıt kaydı silinemedi."));
      setSaveNoticeKind("error");
    } finally {
      setIsLoading(false);
    }
  }, [canDeleteRecords, isOnline, selectedProjectId]);

  const handleDownloadFuelByDate = useCallback(async () => {
    setExportNotice("");

    if (!exportStartDate || !exportEndDate) {
      setExportNotice("İndirme için başlangıç ve bitiş tarihi seçin.");
      return;
    }

    if (exportStartDate > exportEndDate) {
      setExportNotice("Başlangıç tarihi bitiş tarihinden büyük olamaz.");
      return;
    }

    const filteredRows = selectedVehicleRecords.filter(
      (row) => row.date >= exportStartDate && row.date <= exportEndDate
    );

    if (filteredRows.length === 0) {
      setExportNotice("Seçilen tarih aralığında indirilecek yakıt kaydı yok.");
      return;
    }

    try {
      await exportVehicleRowsToExcel({
        fileName: `yakit-kayitlari_${selectedVehicle?.plate || "arac"}_${exportStartDate}_${exportEndDate}.xlsx`,
        worksheetName: "Yakıt Kayıtları",
        columns: [
          { header: "Tarih", key: "date", width: 14 },
          { header: "Plaka", key: "plate", width: 18 },
          { header: "Kaydı Giren", key: "createdByName", width: 24 },
          { header: "Km", key: "km", width: 14 },
          { header: "Yakıt Miktarı", key: "fuelAmount", width: 16 },
          { header: "Fiş No", key: "receiptNo", width: 16 },
          { header: "Toplam Tutar", key: "totalAmount", width: 16 },
          { header: "Fiş Fotoğrafı", key: "receiptPhoto", width: 18 },
        ],
        rows: filteredRows.map((row) => ({
          date: formatDateLabel(row.date),
          plate: selectedVehicle?.plate || "-",
          createdByName: row.createdByName || "-",
          km: toExcelNumber(row.km),
          fuelAmount: toExcelNumber(row.fuelAmount),
          receiptNo: row.receiptNo || "-",
          totalAmount: toExcelNumber(row.totalAmount),
          receiptPhoto: row.receiptImageUrl ? "" : "Yok",
          receiptImageUrl: row.receiptImageUrl || "",
        })),
        imageColumns: [
          {
            columnKey: "receiptPhoto",
            dataKey: "receiptImageUrl",
          },
        ],
      });
      setExportNotice(`${filteredRows.length} yakıt kaydı Excel olarak indirildi.`);
    } catch (error) {
      setExportNotice(getApiErrorMessage(error, "Excel dosyası oluşturulamadı."));
    }
  }, [
    exportEndDate,
    exportStartDate,
    selectedVehicle,
    selectedVehicleRecords,
  ]);

  return (
    <div className="tool-workspace tool-workspace--vehicle">
      <div className="tool-workspace__panel">
        <div className="tool-workspace__header">
          <div>
            <div className="tool-workspace__eyebrow">Araç Modülü</div>
            <h1 className="tool-workspace__title">{title}</h1>
            <p className="tool-workspace__description">
              {description} Yakıt girişi de proje bazlı aktif araç havuzuna
              bağlıdır; pasif araçların eski kayıtları görünür, yeni kayıtları
              kapalıdır.
            </p>
          </div>

          <button
            type="button"
            className="tool-workspace__back-button"
            onClick={onBack}
          >
            Haritaya Dön
          </button>
        </div>

        {!isOnline ? (
          <div className="fuel-page__helper-text fuel-page__save-notice fuel-page__save-notice--info">
            Çevrimdışı: saha noktaları ve ölçümler saklanır; yakıt kilometre zinciri bağlantı gelene kadar salt okunurdur.
          </div>
        ) : null}

        <div className="fuel-page">
          <aside className="fuel-page__sidebar">
            <div className="fuel-page__sidebar-header">
              <h2 className="fuel-page__sidebar-title">Aktif Araçlar</h2>
              <p className="fuel-page__sidebar-text">
                Bu projeye bağlı aktif araçlar için yakıt satırı açabilirsiniz.
              </p>
            </div>

            <div className="fuel-page__vehicle-list">
              {activeVehicles.length > 0 ? (
                activeVehicles.map((vehicle) => (
                  <button
                    key={vehicle.id}
                    type="button"
                    className={`fuel-page__vehicle-button ${
                      selectedVehicleId === vehicle.id
                        ? "fuel-page__vehicle-button--active"
                        : ""
                    }`.trim()}
                    onClick={() => {
                      setSelectedVehicleId(vehicle.id);
                      setSaveNotice("");
                    }}
                  >
                    <span className="fuel-page__vehicle-plate">{vehicle.plate}</span>
                    <span className="fuel-page__vehicle-meta">
                      {
                        records.filter((record) => record.vehicleId === vehicle.id)
                          .length
                      }{" "}
                      yakıt kaydı
                    </span>
                  </button>
                ))
              ) : (
                <div className="fuel-page__empty-state">
                  Bu proje için aktif araç yok. Araç havuzu admin tarafından
                  tanımlanmalıdır.
                </div>
              )}
            </div>

            {archivedVehicles.length > 0 ? (
              <div className="vehicle-km-page__archive-panel">
                <div className="fuel-page__sidebar-header">
                  <h3 className="fuel-page__records-title">Geçmiş Araçlar</h3>
                  <p className="fuel-page__sidebar-text">
                    Pasife alınan araçların eski yakıt kayıtları burada görüntülenir.
                  </p>
                </div>

                <div className="fuel-page__vehicle-list">
                  {archivedVehicles.map((vehicle) => (
                    <button
                      key={vehicle.id}
                      type="button"
                      className={`fuel-page__vehicle-button fuel-page__vehicle-button--archived ${
                        selectedVehicleId === vehicle.id
                          ? "fuel-page__vehicle-button--active"
                          : ""
                      }`.trim()}
                      onClick={() => {
                        setSelectedVehicleId(vehicle.id);
                        setSaveNotice("");
                      }}
                    >
                      <span className="fuel-page__vehicle-plate">{vehicle.plate}</span>
                      <span className="fuel-page__vehicle-meta">Pasif araç</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </aside>

          <section className="fuel-page__content">
            {selectedVehicle ? (
              <>
                <div className="fuel-page__content-header">
                  <div>
                    <div className="fuel-page__content-eyebrow">Yakıt Kaydı</div>
                    <h2 className="fuel-page__content-title">{selectedVehicle.plate}</h2>
                  </div>
                  <div className="fuel-page__content-note">
                    {selectedVehicle.active === false
                      ? "Bu araç pasif. Eski yakıt satırları görünür, yeni kayıt açılamaz."
                      : "Tarih bugün seçili gelir; gerektiğinde değiştirebilirsiniz."}
                  </div>
                </div>

                <div className="fuel-page__form-grid">
                  <div className="fuel-page__field">
                    <label className="fuel-page__field-label" htmlFor="fuel-date-input">Tarih</label>
                    <input
                      id="fuel-date-input"
                      type="date"
                      className="fuel-page__text-input fuel-page__text-input--date"
                      value={recordDate}
                      required
                      onChange={(event) => {
                        setRecordDate(event.target.value);
                        setSaveNotice("");
                      }}
                      disabled={selectedVehicle.active === false || isLoading || !isOnline}
                    />
                  </div>

                  <div className="fuel-page__field">
                    <label className="fuel-page__field-label" htmlFor="fuel-km-input">Km</label>
                    <input
                      id="fuel-km-input"
                      type="text"
                      inputMode="numeric"
                      className="fuel-page__text-input"
                      value={kmDraft}
                      onChange={(event) => {
                        setKmDraft(event.target.value.replace(/[^\d]/g, ""));
                        setSaveNotice("");
                      }}
                      placeholder="Örn. 125640"
                      disabled={selectedVehicle.active === false || isLoading || !isOnline}
                    />
                  </div>

                  <div className="fuel-page__field">
                    <label className="fuel-page__field-label" htmlFor="fuel-amount-input">
                      Yakıt Miktarı
                    </label>
                    <input
                      id="fuel-amount-input"
                      type="text"
                      inputMode="decimal"
                      className="fuel-page__text-input"
                      value={fuelAmountDraft}
                      onChange={(event) => {
                        setFuelAmountDraft(event.target.value);
                        setSaveNotice("");
                      }}
                      placeholder="Örn. 42,50"
                      disabled={selectedVehicle.active === false || isLoading || !isOnline}
                    />
                  </div>

                  <div className="fuel-page__field">
                    <label className="fuel-page__field-label" htmlFor="fuel-receipt-no-input">
                      Fiş No
                    </label>
                    <input
                      id="fuel-receipt-no-input"
                      type="text"
                      className="fuel-page__text-input"
                      value={receiptNoDraft}
                      onChange={(event) => {
                        setReceiptNoDraft(event.target.value);
                        setSaveNotice("");
                      }}
                      placeholder="Örn. 18452"
                      disabled={selectedVehicle.active === false || isLoading || !isOnline}
                    />
                  </div>

                  <div className="fuel-page__field">
                    <label className="fuel-page__field-label" htmlFor="fuel-total-input">
                      Toplam Tutar
                    </label>
                    <input
                      id="fuel-total-input"
                      type="text"
                      inputMode="decimal"
                      className="fuel-page__text-input"
                      value={totalAmountDraft}
                      onChange={(event) => {
                        setTotalAmountDraft(event.target.value);
                        setSaveNotice("");
                      }}
                      placeholder="Örn. 2350,00"
                      disabled={selectedVehicle.active === false || isLoading || !isOnline}
                    />
                  </div>
                </div>

                <div className="fuel-page__photo-panel">
                  <div className="fuel-page__photo-header">
                    <div>
                      <div className="fuel-page__field-label">Fiş Fotoğrafı</div>
                      <div className="fuel-page__photo-text">
                        Kameradan çekebilir veya galeriden seçebilirsiniz.
                      </div>
                    </div>
                    <div className="fuel-page__photo-actions">
                      <label className="fuel-page__photo-button" htmlFor="fuel-receipt-camera-input">
                        Fotoğraf Çek
                      </label>
                      <label
                        className="fuel-page__photo-button fuel-page__photo-button--secondary"
                        htmlFor="fuel-receipt-gallery-input"
                      >
                        Galeriden Seç
                      </label>
                    </div>
                    <input
                      id="fuel-receipt-camera-input"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="fuel-page__file-input"
                      onChange={handleReceiptImageChange}
                      disabled={selectedVehicle.active === false || isLoading || !isOnline}
                    />
                    <input
                      id="fuel-receipt-gallery-input"
                      type="file"
                      accept="image/*"
                      className="fuel-page__file-input"
                      onChange={handleReceiptImageChange}
                      disabled={selectedVehicle.active === false || isLoading || !isOnline}
                    />
                  </div>

                  {receiptImageUrl ? (
                    <div className="fuel-page__photo-preview-card">
                      <img src={receiptImageUrl} alt="Fiş önizleme" className="fuel-page__photo-preview" />
                    </div>
                  ) : (
                    <div className="fuel-page__photo-empty">Henüz fiş fotoğrafı seçilmedi.</div>
                  )}

                  {photoNotice ? (
                    <div className="fuel-page__helper-text">{photoNotice}</div>
                  ) : null}
                </div>

                <div className="fuel-page__actions">
                  <button
                    type="button"
                    className="fuel-page__primary-button"
                    onClick={handleSaveFuelRecord}
                    disabled={selectedVehicle.active === false || isLoading || !isOnline}
                  >
                    Kaydet
                  </button>
                  {saveNotice ? (
                    <div
                      className={`fuel-page__helper-text fuel-page__save-notice fuel-page__save-notice--${saveNoticeKind || "info"}`}
                    >
                      {saveNotice}
                    </div>
                  ) : null}
                </div>

                <div className="fuel-page__records">
                  <div className="fuel-page__records-header">
                    <h3 className="fuel-page__records-title">Kayıt Geçmişi</h3>
                    <span className="fuel-page__records-badge">
                      {selectedVehicleRecords.length} satır
                    </span>
                  </div>

                  <div className="fuel-page__export-panel">
                    <div className="fuel-page__field">
                      <label className="fuel-page__field-label" htmlFor="fuel-export-start-date">
                        Başlangıç Tarihi
                      </label>
                      <input
                        id="fuel-export-start-date"
                        type="date"
                        className="fuel-page__text-input fuel-page__text-input--date"
                        value={exportStartDate}
                        onChange={(event) => {
                          setExportStartDate(event.target.value);
                          setExportNotice("");
                        }}
                      />
                    </div>
                    <div className="fuel-page__field">
                      <label className="fuel-page__field-label" htmlFor="fuel-export-end-date">
                        Bitiş Tarihi
                      </label>
                      <input
                        id="fuel-export-end-date"
                        type="date"
                        className="fuel-page__text-input fuel-page__text-input--date"
                        value={exportEndDate}
                        onChange={(event) => {
                          setExportEndDate(event.target.value);
                          setExportNotice("");
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      className="fuel-page__primary-button fuel-page__export-button"
                      onClick={handleDownloadFuelByDate}
                    >
                      Tarihe Göre Excel İndir
                    </button>
                  </div>

                  {exportNotice ? (
                    <div className="fuel-page__helper-text fuel-page__export-notice">
                      {exportNotice}
                    </div>
                  ) : null}

                  {selectedVehicleRecords.length > 0 ? (
                    <div className="fuel-page__record-list">
                      {selectedVehicleRecords.map((record) => {
                        const canEditRecord =
                          String(record.createdByUserId || "") ===
                          String(currentUserId || "");
                        const isEditingRecord = editRecord?.id === record.id;

                        return (
                        <div key={record.id} className="fuel-page__record-row">
                          <div className="fuel-page__record-cell">
                            <span className="fuel-page__record-label">Tarih</span>
                            <strong>{formatDateLabel(record.date)}</strong>
                          </div>
                          <div className="fuel-page__record-cell">
                            <span className="fuel-page__record-label">Kaydı Giren</span>
                            <strong>{record.createdByName || "-"}</strong>
                          </div>
                          <div className="fuel-page__record-cell">
                            <span className="fuel-page__record-label">Km</span>
                            {isEditingRecord ? (
                              <input
                                className="vehicle-record-edit-input"
                                inputMode="numeric"
                                value={editRecord.km}
                                disabled={!isOnline}
                                onChange={(event) =>
                                  setEditRecord((previous) => ({
                                    ...previous,
                                    km: event.target.value.replace(/[^\d]/g, ""),
                                  }))
                                }
                              />
                            ) : (
                              <strong>{record.km || "-"}</strong>
                            )}
                          </div>
                          <div className="fuel-page__record-cell">
                            <span className="fuel-page__record-label">Yakıt</span>
                            {isEditingRecord ? (
                              <input
                                className="vehicle-record-edit-input"
                                inputMode="decimal"
                                value={editRecord.fuelAmount}
                                disabled={!isOnline}
                                onChange={(event) =>
                                  setEditRecord((previous) => ({
                                    ...previous,
                                    fuelAmount: event.target.value.replace(/[^\d,.]/g, ""),
                                  }))
                                }
                              />
                            ) : (
                              <strong>{record.fuelAmount || "-"}</strong>
                            )}
                          </div>
                          <div className="fuel-page__record-cell">
                            <span className="fuel-page__record-label">Fiş No</span>
                            <strong>{record.receiptNo || "-"}</strong>
                          </div>
                          <div className="fuel-page__record-cell">
                            <span className="fuel-page__record-label">Toplam</span>
                            {isEditingRecord ? (
                              <input
                                className="vehicle-record-edit-input"
                                inputMode="decimal"
                                value={editRecord.totalAmount}
                                disabled={!isOnline}
                                onChange={(event) =>
                                  setEditRecord((previous) => ({
                                    ...previous,
                                    totalAmount: event.target.value.replace(/[^\d,.]/g, ""),
                                  }))
                                }
                              />
                            ) : (
                              <strong>{record.totalAmount || "-"}</strong>
                            )}
                          </div>
                          <div className="fuel-page__record-cell">
                            <span className="fuel-page__record-label">Fiş</span>
                            {record.receiptImageUrl ? (
                              <a
                                href={record.receiptImageUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="fuel-page__receipt-link"
                              >
                                Fotoğrafı Aç
                              </a>
                            ) : (
                              <strong>-</strong>
                            )}
                          </div>
                          {canEditRecord || canDeleteRecords ? (
                            <div className="fuel-page__record-cell vehicle-record-edit-actions">
                              <span className="fuel-page__record-label">İşlem</span>
                              {isEditingRecord ? (
                                <div className="vehicle-record-edit-button-row">
                                  <button
                                    type="button"
                                    className="vehicle-record-edit-button vehicle-record-edit-button--save"
                                    onClick={handleSaveRecordEdit}
                                    disabled={isLoading || !isOnline}
                                  >
                                    Kaydet
                                  </button>
                                  <button
                                    type="button"
                                    className="vehicle-record-edit-button"
                                    onClick={() => setEditRecord(null)}
                                    disabled={isLoading || !isOnline}
                                  >
                                    İptal
                                  </button>
                                </div>
                              ) : (
                                <div className="vehicle-record-edit-button-row">
                                  {canEditRecord ? (
                                    <button
                                      type="button"
                                      className="vehicle-record-edit-button"
                                      onClick={() => handleStartRecordEdit(record)}
                                      disabled={!isOnline}
                                    >
                                      Düzenle
                                    </button>
                                  ) : null}
                                  {canDeleteRecords ? (
                                    <button
                                      type="button"
                                      className="vehicle-record-edit-button vehicle-record-edit-button--delete"
                                      onClick={() => handleDeleteRecord(record)}
                                      disabled={isLoading || !isOnline}
                                    >
                                      Sil
                                    </button>
                                  ) : null}
                                </div>
                              )}
                            </div>
                          ) : null}
                        </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="fuel-page__empty-state fuel-page__empty-state--inline">
                      Bu araç için henüz yakıt satırı yok.
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="fuel-page__empty-state fuel-page__empty-state--content">
                Yakıt kaydı girmek için soldan bir araç seçin.
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
